import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileUploadCard from "./FileUploadCard";
import CoverLetterTextArea from "./CoverLetterTextArea";
import UploadedFilesTable from "./UploadedFilesTable";
import ResumeSelectionList from "./ResumeSelectionList"; // Added ResumeSelectionList import
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import useUserId from "hooks/useUserId";
import { useTranslation } from "react-i18next";
import ResumeHistory from "views/client/ResumeHistory";
import QuizDisplay from "./QuizDisplay";
// import FileUploadCard from "components/FileUploadCard";
// import UploadedFilesTable from "components/UploadedFilesTable";

const ApplicationForm = ({
  selectedFile,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  handleInputChange,
  handleDateChange,
  handleFileUpload,
  uploadedFilesList,
  handleFileSelect,
  handleFileDelete,
  handleShowFile,
  handleSubmit,
  hasApplied,
  register,
  selectedFilename,
  isSubmitting,
  resetTrigger,
  job,
  coverLetterText,
  onCoverLetterChange,
  useMatchingProfile,
  setUseMatchingProfile,
}) => {
  console.log('useMatchingProfile', useMatchingProfile)
  const { t } = useTranslation();
  const userid = useUserId();
  const userId = userid.userId;

  // State for dialog visibility
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    console.log('ApplicationForm received onCoverLetterChange:', typeof onCoverLetterChange, onCoverLetterChange);
    console.log('ApplicationForm received coverLetterText:', coverLetterText);
  }, [onCoverLetterChange, coverLetterText]);

  // Add disabled state that combines user role and application status
  const isDisabled = userid.role === "r" || hasApplied || isSubmitting;

  // State to track if a resume is selected from ResumeSelectionList
  const [isResumeSelected, setIsResumeSelected] = useState(false);
  const [selectedResumeFilename, setSelectedResumeFilename] = useState('');
  const [localCoverLetterText, setLocalCoverLetterText] = useState('');
  const [isProfileCompleteChecked, setIsProfileCompleteChecked] = useState(false);
  useEffect(() => {
    console.log("user role::", userid);
  }, []);

  useEffect(() => {
    // Reset controlled components
    setFormData(prevData => ({
      ...prevData,
      academicDegree: '',
      fieldOfStudy: '',
      lastPostOccupied: '',
      linkedinProfile: '',
      dateOfBirth: '',
      familyStatus: '',
      resumeUploaded: false,
      coverLetterUploaded: false,
      resumeFile: null,

    }));
  }, [resetTrigger, setFormData]);

  const isProfileComplete = async () => {
    try {
      // Check if userId is available
      if (!userId) {
        console.warn("User ID not available for profile completion check");
        return false;
      }

      // Check if REACT_APP_API_URL is defined
      if (!process.env.REACT_APP_API_URL) {
        console.error("REACT_APP_API_URL environment variable is not defined");
        return false;
      }

      const url = `${process.env.REACT_APP_API_URL}authentication/get_candidate_profile_score/${userId}/`;
      console.log("Checking profile completion at:", url);

      const response = await fetch(url);

      // Log response status for debugging
      console.log("Profile completion API response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(`Network response was not ok. Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Profile completion data:", data);
      return data.profile_score || false;
    } catch (error) {
      console.error("Error checking profile completion:", error);
      // Return false to indicate profile check failed, but don't block the form
      return false;
    }
  };

  useEffect(() => {
    const checkProfile = async () => {
      try {
        const result = await isProfileComplete();
        console.log("Profile completion check result:", result);

        // Only set the state if we got a valid result
        if (typeof result === 'number') {
          if (result <= 60) {
            console.log("Profile completion is less than 60%", result);
            setIsProfileCompleteChecked(true);
          } else {
            console.log("Profile completion is 60% or higher", result);
            setIsProfileCompleteChecked(false);
          }
        } else {
          // If we couldn't check the profile, enable the checkbox by default
          setIsProfileCompleteChecked(false);
        }
      } catch (error) {
        console.error("Error in checkProfile useEffect:", error);
        // Enable the checkbox if there's an error checking the profile
        setIsProfileCompleteChecked(false);
      }
    };

    // Only run the profile check if we have a userId
    if (userId) {
      checkProfile();
    } else {
      console.log("No userId available, skipping profile completion check");
      setIsProfileCompleteChecked(false);
    }
  }, [userId]);

  // Custom handler for resume selection from ResumeSelectionList
  const handleResumeSelect = (fileKey, file) => {
    console.log("Resume selected from list:", { fileKey, file });
    // Update the state to indicate a resume is selected
    setIsResumeSelected(true);

    // Extract filename for display
    let filename = '';
    if (typeof file === "string") {
      filename = file.split('/').pop();
    } else if (file && typeof file === "object") {
      filename = file.resume_path ? file.resume_path.split('/').pop() : `Resume ${fileKey}`;
    }
    setSelectedResumeFilename(filename);

    // Call the original handleFileSelect if it exists
    if (handleFileSelect) {
      handleFileSelect(fileKey, file);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {false && (
          <>
            <Grid item xs={12} sm={6} sx={{ marginBottom: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#000000",
                  marginBottom: 2,
                }}
              >
                {t("applicationForm.academicDegree")}
              </Typography>
              <FormControl fullWidth error={!!formErrors.academicDegree}>
                <Select
                  sx={{
                    width: "100%",
                    height: "48px",
                    borderRadius: 2,
                    borderWidth: "1px",
                    padding: "1px 2px",
                    gap: "0px",
                    color: "#D9D9D9",
                    "&.Mui-disabled": {
                      backgroundColor: "#f5f5f5",
                      color: "#9e9e9e",
                    },
                  }}
                  labelId="academic-degree-label"
                  id="academicDegree"
                  name="academicDegree"
                  value={formData.academicDegree}
                  onChange={handleInputChange}
                  disabled={isDisabled}
                >
                  {Object.entries(t("applicationForm.degrees", { returnObjects: true })).map(([key, value]) => (
                    <MenuItem key={key} value={key}>{value}</MenuItem>
                  ))}
                </Select>
                {formErrors.academicDegree && (
                  <FormHelperText>{formErrors.academicDegree}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#000000",
                  marginBottom: 2,
                }}
              >
                {t("applicationForm.fieldOfStudy")}
              </Typography>
              <FormControl fullWidth error={!!formErrors.fieldOfStudy}>
                <Select
                  sx={{
                    width: "100%",
                    height: "48px",
                    borderRadius: 2,
                    borderWidth: "1px",
                    padding: "1px 20px",
                    gap: "10px",
                    color: "#D9D9D9",
                    "&.Mui-disabled": {
                      backgroundColor: "#f5f5f5",
                      color: "#9e9e9e",
                    },
                  }}
                  labelId="field-of-study-label"
                  id="fieldOfStudy"
                  name="fieldOfStudy"
                  value={formData.fieldOfStudy}
                  onChange={handleInputChange}
                  disabled={isDisabled}
                >
                  {Object.entries(t("applicationForm.fields", { returnObjects: true })).map(([key, value]) => (
                    <MenuItem key={key} value={key}>{value}</MenuItem>
                  ))}
                </Select>
                {formErrors.fieldOfStudy && (
                  <FormHelperText>{formErrors.fieldOfStudy}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ marginBottom: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#000000",
                  marginBottom: 2,
                }}
              >
                {t("applicationForm.lastPostOccupied")}
              </Typography>
              <TextField
                sx={{
                  width: "100%",
                  height: "48px",
                  border: "1px solid #D9D9D9",
                  borderRadius: 2,
                  color: "#D9D9D9",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#f5f5f5",
                      color: "#9e9e9e",
                    },
                  },
                }}
                fullWidth
                required
                id="lastPostOccupied"
                name="lastPostOccupied"
                value={formData.lastPostOccupied}
                onChange={handleInputChange}
                error={!!formErrors.lastPostOccupied}
                helperText={formErrors.lastPostOccupied}
                disabled={isDisabled}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#000000",
                  marginBottom: 2,
                }}
              >
                {t("applicationForm.linkedinProfile")}
              </Typography>
              <TextField
                sx={{
                  width: "100%",
                  height: "48px",
                  border: "1px solid #D9D9D9",
                  borderRadius: 2,
                  color: "#D9D9D9",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#f5f5f5",
                      color: "#9e9e9e",
                    },
                  },
                }}
                fullWidth
                id="linkedinProfile"
                name="linkedinProfile"
                value={formData.linkedinProfile}
                onChange={handleInputChange}
                disabled={isDisabled}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ marginBottom: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#000000",
                  marginBottom: 1,
                }}
              >
                {t("applicationForm.dateOfBirth")}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    value={
                      formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null
                    }
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    disabled={isDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        required: true,
                        error: !!formErrors.dateOfBirth,
                        helperText: formErrors.dateOfBirth,
                        sx: {
                          height: "48px",
                          border: "1px solid #D9D9D9",
                          borderRadius: 2,
                          color: "#D9D9D9",
                          "& .MuiOutlinedInput-root": {
                            height: "100%",
                            "& fieldset": {
                              border: "none",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#f5f5f5",
                              color: "#9e9e9e",
                            },
                            "& input": {
                              padding: "8.5px 14px",
                              fontSize: "0.875rem",
                            },
                          },
                        },
                      },
                      inputAdornment: {
                        sx: {
                          "& .MuiButtonBase-root": {
                            padding: 0,
                          },
                        },
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#000000",
                  marginBottom: 2,
                }}
              >
                {t("applicationForm.familyStatus")}
              </Typography>
              <TextField
                sx={{
                  width: "100%",
                  height: "48px",
                  border: "1px solid #D9D9D9",
                  borderRadius: 2,
                  color: "#D9D9D9",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#f5f5f5",
                      color: "#9e9e9e",
                    },
                  },
                }}
                fullWidth
                id="familyStatus"
                name="familyStatus"
                value={formData.familyStatus}
                onChange={handleInputChange}
                disabled={isDisabled}
              />
            </Grid>
          </>
        )}

        <Grid
          container
          spacing={3}
          sx={{ paddingLeft: { xs: 0, sm: "2%" }, paddingTop: 4 }}
        >
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  // disabled={isProfileCompleteChecked}
                  checked={useMatchingProfile || false}
                  onChange={(e) => {
                    console.log('Checkbox clicked, setUseMatchingProfile:', typeof setUseMatchingProfile);
                    // If profile is incomplete, show the dialog
                    if (isProfileCompleteChecked) {
                      setOpenDialog(true);
                    } else if (typeof setUseMatchingProfile === 'function') {
                      setUseMatchingProfile(e.target.checked);
                    } else {
                      console.warn('setUseMatchingProfile is not a function');
                    }
                  }}
                  sx={{
                    color: "#0017E7",
                    '&.Mui-checked': {
                      color: "#0017E7",
                    },
                  }}
                />
              }
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      color: "#000000",
                      fontSize: "14px",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                    }}
                  >
                    {t("applicationForm.useMatchingProfile") || "Use my matching profile instead of uploading resume"}
                  </Typography>
                  <Tooltip
                    title="Cette fonctionnalité permet de postuler automatiquement en utilisant les informations extraites de votre CV. Grâce à cette option, notre système intelligent analyse votre profil, pré-remplit les champs nécessaires et simplifie le processus de candidature pour vous faire gagner du temps et améliorer votre efficacité."
                    placement="top"
                    arrow
                  >
                    <IconButton sx={{ ml: 1, p: 0 }}>
                      <HelpOutlineIcon sx={{ fontSize: 16, color: '#0017E7' }} />
                    </IconButton>
                  </Tooltip>
                </div>
              }
            />

            {/* Quiz Display Component */}
            {/* {job && job.id && (
              <Box sx={{ mt: 2, width: '100%' }}>
                <QuizDisplay
                  postId={job.id}
                  onAnswersChange={(answers) => {
                    console.log('Quiz Answers:', JSON.stringify(answers, null, 2));
                  }}
                />
              </Box>
            )} */}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              sx={{
                color: "#0000004D",
                fontSize: "13px",
                fontFamily: "Poppins",
                fontWeight: 500,
                mb: 1,
              }}
            >
              {t("applicationForm.resume")}*
            </Typography>
            <FileUploadCard
              type="resume"
              isUploaded={formData.resumeUploaded || isResumeSelected}
              register={register}
              handleFileUpload={handleFileUpload}
              disabled={useMatchingProfile}
              selectedFile={selectedFile}
              selectedFilename={formData.resumeUploaded ? selectedFilename : selectedResumeFilename}
            />

            {/* Added ResumeSelectionList component */}

          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              sx={{
                color: "#0000004D",
                fontSize: "13px",
                fontFamily: "Poppins",
                fontWeight: 500,
                mb: 1,
              }}
            >
              {t("applicationForm.coverLetter")}
            </Typography>
            <CoverLetterTextArea
              value={localCoverLetterText}
              onChange={(e) => {
                console.log('Setting localCoverLetterText to:', e.target.value);
                setLocalCoverLetterText(e.target.value);
                if (onCoverLetterChange) {
                  onCoverLetterChange(e);
                }
              }}
              placeholder={t("applicationForm.coverLetterPlaceholder")}
              disabled={false}
            />
          </Grid>
          {/* Pass the custom handler to ResumeSelectionList */}
          <ResumeSelectionList handleFileSelect={handleResumeSelect} />
        </Grid>

        {!job.scraped ? <Grid item xs={12}>
          <UploadedFilesTable
            uploadedFilesList={uploadedFilesList}
            handleFileSelect={handleFileSelect}
            handleFileDelete={handleFileDelete}
            handleShowFile={handleShowFile}
            selectedFile={selectedFile}
            hasApplied={hasApplied}
            disabled={isDisabled}
          />
        </Grid> : null}

        {/* Dialog for incomplete profile */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            color: "#0017E7",
            pb: 1
          }}>
            {t("applicationForm.incompleteProfileTitle") || "Profil Incomplet"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#000000",
                  mb: 2
                }}
              >
                {t("applicationForm.incompleteProfileMessage") ||
                  "Pour utiliser la fonctionnalité de candidature avec votre profil intelligent, vous devez d'abord compléter votre profil à au moins 60%."}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#000000",
                  mb: 1
                }}
              >
                {t("applicationForm.incompleteProfileInstructions") ||
                  "Veuillez suivre ces étapes pour compléter votre profil :"}
              </Typography>
              <Typography
                component="ol"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#000000",
                  pl: 2,
                  mt: 1,
                  mb: 2
                }}
              >
                <li>{t("applicationForm.incompleteProfileStep1") || "Allez dans Profil > Mon Smart CV"}</li>
                <li>{t("applicationForm.incompleteProfileStep2") || "Créez votre Smart CV en téléchargeant un CV ou en saisissant manuellement vos informations professionnelles"}</li>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="contained"
              sx={{
                backgroundColor: "#0017E7",
                color: "#FFFFFF",
                fontFamily: "Poppins",
                fontWeight: 500,
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                '&:hover': {
                  backgroundColor: "#0017E7",
                  opacity: 0.9
                }
              }}
            >
              {t("applicationForm.understood") || "Compris"}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </form>
  );
};

export default ApplicationForm;